import {
    Component, OnInit, OnDestroy, OnChanges, Input, ViewChild,
    ElementRef, ChangeDetectorRef, ViewRef, ViewEncapsulation
} from '@angular/core';
// import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OwlCarousel } from 'ngx-owl-carousel';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ISubscription } from 'rxjs/Subscription';
import { Manuscript } from '../../../models/manuscript';
import { ManuscriptsService } from '../../../services/manuscripts.service';
import { MsImageLinkSelection } from '../../../models/ms-image-link';
import { environment } from '../../../../environments/environment';
import { ImageGetNamePipe } from '../../../pipes/image-get-name.pipe';
import { MsImageFileList } from '../../../models/ms-image-file';

export const IMAGES_LOADED_AT_A_TIME = 30;

@Component({
  selector: 'app-ms-image-gallery',
  templateUrl: './ms-image-gallery.component.html',
  styleUrls: ['./ms-image-gallery.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class MsImageGalleryComponent implements OnInit, OnChanges, OnDestroy {
    @Input() expandedImage: boolean;
    @ViewChild('owlElement') owlElement: OwlCarousel;
    @ViewChild('imageFrame') imageFrame: ElementRef;
    private subscriptions: Array<ISubscription> = [];
    private manuscript: Manuscript;
    // private imageLinks: MsImageLinkList;
    // viewerImageUrl: SafeResourceUrl;
    allImages: Array<String>;
    loadedImages: Array<String>;
    private loadingImages: Array<Boolean>;
    loadedImagesCount = 0;
    imageLinkSelection: MsImageLinkSelection;
    carouselOptions = {
        autoplay: false,
        autoplayTimeout: 2000,
        autoplayHoverPause: true,
        loop: false,
        onChanged : this.carouselEvent.bind(this),
        onDragged : this.carouselEvent.bind(this),
        margin: 5,
        dots: false,
        nav: true,
        navElement: 'div',
        navText: [
            '<i class="fa fa-arrow-left"></i> Previous',
            ' Next <i class="fa fa-arrow-right"></i>'
        ],
        responsive: {
            0: {
                items: 1
            },
            600: {
                items: 2
            },
            1000: {
                items: 3
            },
            1200: {
                items: 3
            }
        }
    };
    selectImageData$: BehaviorSubject<MsImageFileList>;
    selectedImage: String;

    constructor(
        private manuscriptsService: ManuscriptsService,
        // private domSanitizer: DomSanitizer,
        private imageGetNamePipe: ImageGetNamePipe,
        private changeDetector: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        this.allImages = [];
        this.loadedImages = [];
        this.loadingImages = [];
        this.selectImageData$ = new BehaviorSubject<MsImageFileList>([]);
        this.subscriptions.push(
            this.manuscriptsService.getManuscript()
                .subscribe(response => this.manuscript = response, err => console.error(err)),
            this.manuscriptsService.getManuscriptImageFiles()
                .subscribe(
                    response => {
                        if (response) {
                            this.allImages = response.map(imgFile => imgFile.image);
                            this.updateViewer(this.allImages[0]);
                            this.loadNextImages(true);
                            this.selectImageData$.next(response);
                        }
                    },
                    err => console.error(err)
                ),
            this.manuscriptsService.getManuscriptImageLinkSelection()
                .subscribe(
                    response => {
                        this.imageLinkSelection = response;
                        this.highlightSelection();
                    },
                    err => console.error(err)
                )
        );
    }

    ngOnChanges(): void {
        if (this.expandedImage) {
            this.carouselOptions.responsive[1200].items = 5;
        } else {
            this.carouselOptions.responsive[1200].items = 3;
        }
        this.owlElement.trigger('refresh.owl.carousel');
    }

    loadNextImages(reset: boolean = false): Array<String> {
        if (reset) {
            this.loadedImages = [];
        }
        const newLoadedImages = this.allImages.slice(this.loadedImages.length, this.loadedImages.length + IMAGES_LOADED_AT_A_TIME);
        this.loadedImages.push(...newLoadedImages);
        return newLoadedImages;
    }

    carouselAddImages(newImages: Array<String>): void {
        for (const newImage of newImages) {
            this.owlElement.trigger('add.owl.carousel', <any> newImage);
        }
    }

    carouselEvent(event: any): void {
        if (
            event.item.index  // position of the current item
            && event.item.count // number of items
            && event.item.index >= (event.item.count - event.page.size)
        ) {
            this.carouselAddImages(this.loadNextImages());
        }
    }

    updateViewer(image: String, annotation: number = 0): void {
        if (image && this.manuscript.pkid) {
//            this.viewerImageUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
//                environment.endpoints.res + '/images/' + image + '/viewer?'
//                + 'options={"noEdit":1,"noLinks":1,"fkidmanuscripts":' + this.manuscript.pkid
//                + (annotation > 0 ? ',"highlight":' + annotation : '')
//                + ',"headers":{"X-PhiBor-App":"' + environment.phiborApp + '"}}'
//                + '&token=' + environment.apiKey
//                + '&label=' + this.imageGetNamePipe.transform(image)
//            );
            if (!(this.changeDetector as ViewRef).destroyed) {
                this.changeDetector.detectChanges();
                if (this.imageFrame && this.imageFrame.nativeElement.contentWindow) {
                    this.imageFrame.nativeElement.contentWindow.location.replace(environment.endpoints.res + '/images/' + image + '/viewer?'
                        + 'options={"noEdit":1,"noLinks":1,"fkidmanuscripts":' + this.manuscript.pkid
                        + (annotation > 0 ? ',"highlight":' + annotation : '')
                        + ',"headers":{"X-PhiBor-App":"' + environment.phiborApp + '"}}'
                        + '&token=' + environment.apiKey
                        + '&label=' + this.imageGetNamePipe.transform(image));
                }
            }
        }
    }

    loadImagesBeforePosition(imagePosition: number) {
        const newLoadedImages = [];
        while (this.loadedImages.length < imagePosition) {
            newLoadedImages.push(...this.loadNextImages());
        }
        this.carouselAddImages(newLoadedImages);
    }

    highlightSelection(): void {
        const selectedImages = this.imageLinkSelection.images;
        if (selectedImages.length > 0) {
            const firstSelectedPos = this.allImages.indexOf(selectedImages[0]);
            const lastSelectedPos = this.allImages.indexOf(selectedImages[selectedImages.length - 1]);

            if (firstSelectedPos !== -1 && lastSelectedPos !== -1) {
                /* Load missing images */
                this.loadImagesBeforePosition(lastSelectedPos);

                /* Move carousel to first image */
                this.owlElement.to([firstSelectedPos]);

                /* Load first image into viewer */
                this.updateViewer(this.allImages[firstSelectedPos], this.imageLinkSelection.annotation);
            }
        }
    }

    clearSelection(): void {
        this.imageLinkSelection.reset();
    }

    updateLoadingImages(imagePos: number, loading: boolean): void {
        this.loadingImages[imagePos] = loading;
        this.loadedImagesCount = this.loadedImages.length - this.loadingImages.filter(loadImg => loadImg === true).length;
    }

    loadSelectedImage(): void {
        if (this.selectedImage) {
            const imagePos = this.allImages.indexOf(this.selectedImage);

            this.clearSelection();

            /* Load missing images */
            this.loadImagesBeforePosition(imagePos);

            /* Move carousel to first image */
            this.owlElement.to([imagePos]);

            /* Load the found image into viewer */
            this.updateViewer(this.allImages[imagePos]);
        } else {
            this.selectedImage = null;
        }
    }

    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            if (subscription) {
                subscription.unsubscribe();
            }
        }
    }

}



// WEBPACK FOOTER //
// ./src/app/components/ui/ms-image-gallery/ms-image-gallery.component.ts